import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES } from "../data/content";
import PageHeader from "../components/PageHeader";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";

export default function Services() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [hash]);

  return (
    <>
      <PageHeader
        eyebrow="[ Expertise ]"
        titleLines={["Quatre leviers", "de performance."]}
        intro="Du chantier Kaizen à la Smart Factory, chaque intervention est pensée pour un impact mesurable sur vos coûts, votre qualité et vos flux."
      />

      <div className="bg-paper">
        {SERVICES.map((s, i) => {
          const dark = i % 2 === 1;
          return (
            <section
              key={s.id}
              id={s.id}
              data-testid={`service-section-${s.id}`}
              className={`scroll-mt-24 py-24 md:py-32 relative ${dark ? "bg-ink-900 text-paper grain" : "bg-paper text-ink"}`}
            >
              <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  <Reveal>
                    <div className="relative overflow-hidden rounded-sm group">
                      <img src={s.image} alt={s.title} className="w-full h-[420px] md:h-[520px] object-cover group-hover:scale-105 transition-transform duration-[1200ms] ease-out" />
                      <div className="absolute top-5 left-5 font-mono-tech text-6xl md:text-8xl font-medium text-white/80 mix-blend-overlay">{s.index}</div>
                    </div>
                  </Reveal>
                </div>
                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  <Reveal>
                    <span className="label-mono text-gold">Service {s.index}</span>
                    <h2 className="font-display text-4xl md:text-6xl font-semibold mt-4 tracking-tight leading-[0.95]">{s.title}</h2>
                    <p className={`text-lg mt-5 max-w-xl ${dark ? "text-paper/70" : "text-ink/60"}`}>{s.description}</p>
                  </Reveal>
                  <Stagger className="grid sm:grid-cols-2 gap-x-8 gap-y-3 mt-9">
                    {s.items.map((it) => (
                      <StaggerItem key={it} className={`flex items-start gap-3 py-2 border-b ${dark ? "hairline-light" : "hairline"}`}>
                        <Check size={16} className="text-gold mt-1 shrink-0" />
                        <span className={dark ? "text-paper/85" : "text-ink/80"}>{it}</span>
                      </StaggerItem>
                    ))}
                  </Stagger>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <section className="bg-ink-950 text-paper grain relative py-20 md:py-28" data-testid="services-cta">
        <div className="relative z-10 mx-auto max-w-[1400px] px-5 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <h2 className="font-display text-3xl md:text-5xl font-semibold tracking-tight max-w-xl">
            Un besoin spécifique ? Construisons votre plan sur mesure.
          </h2>
          <Link to="/contact" data-testid="services-contact-cta" className="group inline-flex items-center gap-3 rounded-full bg-gold text-ink px-8 py-4 label-mono hover:bg-paper transition-colors duration-300 shrink-0">
            Nous contacter <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
          </Link>
        </div>
      </section>
    </>
  );
}
